import React from "react";
import * as yup from "yup";
import { useQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, PLAN_CODES, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { unwrapOr } from "../../utilities/general";
import { getAccountId } from "../../utilities/localStorage";
import {
  CURRENCY_DEFAULT,
  CURRENCY_SYMBOL_DEFAULT,
} from "../../consts/general";

const requestSchema = yup.object({
  accountId: yup.number().nullable(),
});

const responseSchema = yup.object({
  id: yup.number().required(),
  adminId: yup.number().required(),
  isPosEnabled: yup.bool().required(),
  isInboxEnabled: yup.bool().required(),
  paymentSystem: yup.string().nullable(),
  subscription: yup
    .object({
      planCode: yup.string().nullable(),
    })
    .required(),
  subscriptionPlan: yup.string().nullable(),
  preferences: yup.object({
    clientGlobalName: yup.string().nullable(),
    timeFormat: yup.string().nullable(),
    isTouchMd: yup.bool().required(),
    isWalletExpirationConfigured: yup.bool().required(),
    weightUnit: yup.string().nullable(),
    lengthUnit: yup.string().nullable(),
    temperatureUnit: yup.string().nullable(),
    requireAppointmentDob: yup.bool().required(),
  }),
  businessInfo: yup
    .object({
      freeTaxableDocName: yup.string().nullable(),
      freeTaxableDocUrl: yup.string().nullable(),
    })
    .required(),
  integrations: yup
    .object({
      isQualiphyEnabled: yup.bool().required(),
      isShopifyEnabled: yup.bool().required(),
    })
    .required(),
  globalSettings: yup
    .object({
      isPosEnabled: yup.bool().required(),
      isTwilioEnabled: yup.bool().required(),
      posType: yup.string().nullable(),
      accountStatus: yup.string().nullable(),
      currency: yup.string().required(),
      currencySymbol: yup.string().required(),
      twoFactorVerificationSetup: yup
        .object({
          needsSetup: yup.bool().required(),
          canSkip: yup.bool().required(),
        })
        .required(),
      featureFlag: yup.object({
        appointmentDowntime: yup.boolean(),
        betaFeatures: yup.boolean(),
        efax: yup.boolean(),
        inbox: yup.boolean(),
        newBi: yup.boolean(),
        quickbooks: yup.boolean(),
        customTax: yup.boolean(),
        pfi: yup.boolean(),
        sunbit: yup.boolean(),
      }),
      isEfaxEnabled: yup.bool().required(),
      twoFactorTrustDays: yup.number().nullable(),
    })
    .required(),
  createdAt: yup.string().required(),
});

const composeResponse = (res) => {
  const data = unwrapOr(() => res.data.data, {});
  const globalSettings = unwrapOr(() => res.data.global_settings, {});
  const preferences = data?.account_preference || {};
  const businessInfo = data?.account_business_info || {};

  const isAccelerator =
    data?.account_subscription?.plan_code === PLAN_CODES.accelerator;

  const isTwilioEnabled = globalSettings?.twilio_config === 1;

  return {
    id: data.id,
    adminId: data.admin_id,
    isPosEnabled: Boolean(data.pos_enabled),
    isInboxEnabled: isAccelerator && isTwilioEnabled,
    paymentSystem: data.pos_gateway || null,
    subscription: {
      planCode: data?.account_subscription?.plan_code || null,
    },
    subscriptionPlan: data?.account_subscription?.plan_code,
    preferences: {
      isTouchMd: Boolean(preferences.is_touch_md),
      clientGlobalName: preferences.client_replacement_text || null,
      timeFormat: preferences.time_format_original || null,
      isWalletExpirationConfigured:
        (preferences.wallet_item_expiry > 0 &&
          preferences.wallet_item_expiry !== 50000) ||
        (preferences.wallet_dollar_expiry > 0 &&
          preferences.wallet_dollar_expiry !== 50000),
      weightUnit: preferences.weight_unit,
      lengthUnit: preferences.length_unit,
      temperatureUnit: preferences.temperature_unit,
      requireAppointmentDob: Boolean(preferences.require_appointment_dob),
    },
    businessInfo: {
      freeTaxableDocName: businessInfo.free_taxable_doc,
      freeTaxableDocUrl: businessInfo.free_taxable_doc_url,
    },
    integrations: {
      isQualiphyEnabled: Boolean(globalSettings.qualiphy),
      isShopifyEnabled: Boolean(globalSettings.shopify_enabled),
    },
    globalSettings: {
      isPosEnabled: Boolean(globalSettings?.pos_data?.is_pos_enabled),
      isTwilioEnabled,
      posType: globalSettings?.pos_data?.stripe_connection || null,
      accountStatus: globalSettings?.pos_data?.account_status || null,
      currency: globalSettings?.pos_data?.currency_code || CURRENCY_DEFAULT,
      currencySymbol:
        globalSettings?.pos_data?.currency_symbol || CURRENCY_SYMBOL_DEFAULT,
      featureFlag: {
        appointmentDowntime: globalSettings?.feature_flag?.appointment_downtime,
        betaFeatures: globalSettings?.feature_flag?.beta_features,
        efax: globalSettings?.feature_flag?.efax,
        inbox: globalSettings?.feature_flag?.inbox,
        newBi: globalSettings?.feature_flag?.new_bi,
        quickbooks: globalSettings?.feature_flag?.quickbooks,
        customTax: globalSettings?.feature_flag?.custom_tax,
        pfi: globalSettings?.feature_flag?.pfi,
        sunbit: globalSettings?.feature_flag?.sunbit,
        membershipMinTerm:
          globalSettings?.feature_flag?.membership_enforce_min_term,
      },
      isEfaxEnabled: Boolean(globalSettings?.efax_integration),
      twoFactorVerificationSetup: {
        needsSetup: Boolean(globalSettings?.needs_to_set_passcode_or_sq),
        canSkip: Boolean(globalSettings?.can_postpone_passcode_or_sq_popup),
      },
      twoFactorTrustDays: globalSettings?.["2fa_trust_device_days"],
    },
    createdAt: data.created,
    type: globalSettings.pos_data?.account_type,
    stripeTaxAddressPrompt: data.stripe_tax_address_prompt,
    countryList: data.country_list || [],
  };
};

function createAccountQueryKey({ accountId }) {
  return [QUERY_KEYS.account, accountId];
}

function useAccountQuery({ payload, options = {} }) {
  const req = requestSchema.validateSync(payload, {
    strict: true,
  });
  return useQuery(
    createAccountQueryKey({ accountId: req.accountId }),
    async () => {
      const res = await http.get(HTTP_ENDPOINTS.getAccount(req.accountId), {
        params: {
          scopes:
            "accountPreference,activeUsers,accountSubscription,account_business_info",
        },
      });
      return responseSchema.validateSync(composeResponse(res), {
        strict: true,
      });
    },
    options,
  );
}

export const useCurrentAccountQuery = (options = {}) => {
  const accountId = Number(getAccountId());
  return useAccountQuery({
    payload: {
      accountId,
    },
    options: {
      enabled: Boolean(accountId),
      ...options,
    },
  });
};

export const withCurrentAccountQuery = (Component) => {
  return function Wrapper(props) {
    const currentAccountQuery = useCurrentAccountQuery();
    return <Component {...props} currentAccountQuery={currentAccountQuery} />;
  };
};
